/**
 * SPEC.md 解析（纯函数，供 Plan 模式五字段渲染与测试使用）。
 *
 * 对齐后端 backend/app/domain/spec.py 的渲染/解析约定：
 * - 标题行 `# SPEC.md — 名称`（可选）
 * - 五个字段节：`## Why` / `## Capabilities` / `## Constraints` / `## Non-goals`
 *   / `## Success signal`（大小写不敏感）
 * - 列表字段为 `- item` 行；Why 为段落文本
 * - 澄清标记 `[NEEDS CLARIFICATION: ...]` 需要高亮
 */

export const SPEC_SECTION_HEADINGS = [
  "why",
  "capabilities",
  "constraints",
  "non_goals",
  "success_signal",
] as const;

export type SpecSectionKey = (typeof SPEC_SECTION_HEADINGS)[number];

export interface ParsedSpec {
  name: string;
  why: string;
  capabilities: string[];
  constraints: string[];
  nonGoals: string[];
  successSignal: string[];
  /** 全部字段中出现的 [NEEDS CLARIFICATION: ...] 问题列表 */
  clarifications: string[];
}

const TITLE_RE = /^#\s*SPEC\.md\s*(?:[—-]\s*)?(.*)$/im;
const HEADING_RE = /^##\s+(.+)$/i;
const LIST_ITEM_RE = /^[-*]\s+(.+)$/;
const CLARIFICATION_RE = /\[NEEDS CLARIFICATION:\s*([^\]]+)\]/g;

/** 把后端渲染的字段名（capabilities 等）映射为前端 camelCase 键。 */
const SECTION_KEYS: Record<string, keyof ParsedSpec> = {
  why: "why",
  capabilities: "capabilities",
  constraints: "constraints",
  "non-goals": "nonGoals",
  "non_goals": "nonGoals",
  "success signal": "successSignal",
  "success_signal": "successSignal",
};

const EMPTY_SPEC: ParsedSpec = {
  name: "",
  why: "",
  capabilities: [],
  constraints: [],
  nonGoals: [],
  successSignal: [],
  clarifications: [],
};

export function extractClarifications(text: string): string[] {
  const found: string[] = [];
  for (const match of text.matchAll(CLARIFICATION_RE)) {
    found.push(match[1].trim());
  }
  return found;
}

/** 解析 SPEC.md 文本为五字段结构。解析容错：缺失字段保持空值。 */
export function parseSpecMarkdown(markdown: string): ParsedSpec {
  if (!markdown || !markdown.trim()) {
    // 深拷贝数组，避免跨调用共享引用
    return {
      ...EMPTY_SPEC,
      capabilities: [],
      constraints: [],
      nonGoals: [],
      successSignal: [],
      clarifications: [],
    };
  }

  const lines = markdown.split(/\r?\n/);
  const spec: ParsedSpec = {
    ...EMPTY_SPEC,
    capabilities: [],
    constraints: [],
    nonGoals: [],
    successSignal: [],
    clarifications: [],
  };

  // 标题（可选）
  const titleMatch = TITLE_RE.exec(markdown);
  if (titleMatch) spec.name = titleMatch[1].trim();

  let currentSection: keyof ParsedSpec | null = null;
  let listBuffer: string[] = [];

  const flushList = () => {
    if (currentSection && currentSection !== "why" && listBuffer.length > 0) {
      (spec[currentSection] as string[]).push(...listBuffer);
    }
    listBuffer = [];
  };

  for (const rawLine of lines) {
    const headingMatch = HEADING_RE.exec(rawLine.trim());
    if (headingMatch) {
      flushList();
      const key = SECTION_KEYS[headingMatch[1].trim().toLowerCase()];
      currentSection = key ?? null;
      continue;
    }
    if (!currentSection) continue;

    const itemMatch = LIST_ITEM_RE.exec(rawLine.trim());
    if (currentSection === "why") {
      const trimmed = rawLine.trim();
      if (trimmed) {
        spec.why = spec.why ? `${spec.why}\n${trimmed}` : trimmed;
      }
    } else if (itemMatch) {
      listBuffer.push(itemMatch[1].trim());
    }
  }
  flushList();

  spec.clarifications = extractClarifications(markdown);
  return spec;
}

/** 判断文本中是否包含澄清标记。 */
export function hasClarification(text: string): boolean {
  return CLARIFICATION_RE.test(text);
}
